import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { clientPortalService } from '../../../services/clientPortal.service';
import { useCart } from '../../../contexts/CartContext';
import { toDateOnly, parseDateOnly, timeWindows } from './checkoutHelpers';
import { buildCapMap, cartToLines, findOverCapLines } from '../../../lib/overOrder';

// Owns all state + side effects for the Checkout page: profile + delivery
// calendar loading, date-driven availability revalidation, and order submit.
export const useCheckout = () => {
  const navigate = useNavigate();
  const {
    cart,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartSavings,
    isProductAvailableForDate,
    formatAvailableDays,
    getDayName,
    selectedDeliveryDate,
    setSelectedDeliveryDate,
    setAvailableProductIds,
    revalidateCart,
    setItemQuantity,
  } = useCart();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pickableSet, setPickableSet] = useState(new Set());

  const [selectedLocation, setSelectedLocation] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [deliveryTime, setDeliveryTime] = useState(timeWindows[1]);
  const [poNumber, setPoNumber] = useState('');
  const [orderNotes, setOrderNotes] = useState('');

  // Over-ordering controls: the ceiling on each product, whether this account's
  // orders are held above it, and the reason the client gives for keeping a
  // line that passes it.
  const [capInsights, setCapInsights] = useState(null);
  const [capReason, setCapReason] = useState('');
  const [capNote, setCapNote] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect if cart is empty
  useEffect(() => {
    if (!loading && cart.length === 0) {
      toast.error('No items in cart');
      navigate('/client/shop');
    }
  }, [cart, loading, navigate]);

  // When the delivery date changes, re-resolve availability for that date and
  // auto-remove any cart items the new date can't fulfill.
  useEffect(() => {
    if (!deliveryDate) return;
    setSelectedDeliveryDate(deliveryDate);
    refreshAvailabilityForDate(deliveryDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDate]);

  // Re-read the ceilings whenever the delivery date moves: a cap is read from
  // settled sales for that date's product list, not a global figure.
  useEffect(() => {
    let active = true;
    const dateStr = toDateOnly(deliveryDate);
    if (!dateStr) { setCapInsights(null); return undefined; }
    (async () => {
      try {
        const res = await clientPortalService.getOrderInsights(dateStr);
        if (active) setCapInsights(res);
      } catch {
        // The warning is a courtesy — never block checkout on it. The server
        // stays authoritative when the order is placed.
        if (active) setCapInsights(null);
      }
    })();
    return () => { active = false; };
  }, [deliveryDate]);

  const capMap = useMemo(() => buildCapMap(capInsights), [capInsights]);
  const overCapLines = useMemo(
    () => (capInsights?.warnings_enabled === false && !capInsights?.gating?.active
      ? []
      : findOverCapLines(cartToLines(cart), capMap)),
    [cart, capMap, capInsights],
  );
  const capGatingActive = !!capInsights?.gating?.active;
  // A reason is only demanded of a client whose approval gate is on.
  const capReasonRequired = capGatingActive && overCapLines.length > 0;
  const capReasonMissing = capReasonRequired && !capReason;

  const refreshAvailabilityForDate = async (date) => {
    const dateStr = toDateOnly(date);
    if (!dateStr) return;
    try {
      const data = await clientPortalService.getProducts({ delivery_date: dateStr });
      const ids = data.map((p) => p.id);
      setAvailableProductIds(ids);
      const removed = revalidateCart(ids);
      if (removed.length > 0) {
        toast.warning(
          `Removed ${removed.length} item(s) not available on ${format(date, 'EEE, MMM d')}: ${removed.map((r) => r.name).join(', ')}`
        );
      }
    } catch (e) {
      console.error('Error resolving availability:', e);
    }
  };

  const loadData = async () => {
    try {
      const [profileData, cal] = await Promise.all([
        clientPortalService.getProfile(),
        clientPortalService.getDeliveryCalendar(8),
      ]);
      setProfile(profileData);

      const set = new Set((cal?.dates || []).map((d) => d.date));
      setPickableSet(set);

      if (profileData.locations && profileData.locations.length > 0) {
        setSelectedLocation(profileData.locations[0].id);
      }

      // Prefer the date chosen on the Shop page; else first pickable date.
      const prev = selectedDeliveryDate ? toDateOnly(selectedDeliveryDate) : null;
      let initial = null;
      if (prev && set.has(prev)) {
        initial = selectedDeliveryDate;
      } else if (cal?.dates?.length) {
        initial = parseDateOnly(cal.dates[0].date);
      }
      if (initial) setDeliveryDate(initial);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load checkout data');
    } finally {
      setLoading(false);
    }
  };

  const cartTotal = getCartTotal();
  const minimumOrder = profile?.account?.preferences?.minimum_order_amount ?? 50;

  // Get unavailable items for the selected delivery date
  const unavailableItems = deliveryDate
    ? cart.filter((item) => !isProductAvailableForDate(item, deliveryDate))
    : [];
  const hasUnavailableItems = unavailableItems.length > 0;

  const isValidOrder = cartTotal >= minimumOrder && !hasUnavailableItems && !capReasonMissing;

  const selectedLocationData = profile?.locations?.find((loc) => loc.id === selectedLocation);

  const handleRemoveItem = (productId) => {
    removeFromCart(productId);
    if (cart.length === 1) {
      // This was the last item being removed
      toast.info('Cart is empty, redirecting to shop');
      navigate('/client/shop');
    }
  };

  const handleSubmitOrder = async () => {
    if (!selectedLocation) {
      toast.error('Please select a delivery location');
      return;
    }
    if (!deliveryDate) {
      toast.error('Please select a delivery date');
      return;
    }
    if (!isValidOrder) {
      if (capReasonMissing) {
        setReasonTouched(true);
        toast.error('Pick a reason for the lines above your best day, or drop them to the cap');
        return;
      }
      toast.error(`Order minimum is $${minimumOrder.toFixed(2)}`);
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        items: cart.map((item) => ({
          product_id: item.id,
          product_name: item.name,
          pack_size: item.pack_size,
          quantity: item.quantity,
          // Use discounted price if available, otherwise wholesale price
          unit_price: item.your_price || item.wholesale_price,
          subtotal: (item.your_price || item.wholesale_price) * item.quantity,
          // Include discount tracking fields
          original_price: item.wholesale_price,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          has_discount: item.has_discount || false,
          image_url: item.image_url || null,
        })),
        delivery_date: toDateOnly(deliveryDate),
        delivery_time_window: deliveryTime,
        location_id: selectedLocation,
        delivery_address: selectedLocationData
          ? `${selectedLocationData.address}, ${selectedLocationData.city}, ${selectedLocationData.state} ${selectedLocationData.zip_code}`
          : '',
        po_number: poNumber || null,
        order_notes: orderNotes || null,
        total_savings: getCartSavings(),
        // Why a line above the client's own best day is being kept. Recorded
        // whenever one exists; required only when approval is switched on.
        ...(overCapLines.length > 0 && capReason
          ? { over_cap_reason: capReason, over_cap_note: capNote || null }
          : {}),
      };

      const result = await clientPortalService.createOrder(orderData);

      // Clear cart using context
      clearCart();

      if (result.status === 'pending_approval') {
        toast.success(`Order ${result.order_number} submitted — held for our approval`, {
          description: result.over_cap?.approval_deadline
            ? `We confirm by ${result.over_cap.approval_deadline}. Nothing goes to the kitchen until we do.`
            : 'We confirm before the ordering deadline for that date.',
          duration: 10000,
        });
        navigate('/client/orders');
        return;
      }

      toast.success(`Order ${result.order_number} submitted successfully!`, {
        description: 'Order this regularly? Turn it into a recurring schedule.',
        action: {
          label: 'Make it recurring',
          onClick: () => navigate(`/client/recurring-orders?prefill=${result.id}`),
        },
        duration: 10000,
      });
      navigate('/client/orders');
    } catch (error) {
      console.error('Error submitting order:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit order';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const allowedDeliveryDays = profile?.account?.preferences?.allowed_delivery_days || [];

  // Pickable dates come from the backend delivery-calendar (single source of
  // truth: weekly schedule + configured lead time + delivery_override dates).
  const isDateDisabled = (date) => !pickableSet.has(toDateOnly(date));

  // "No delivery days configured" only matters when the account has no schedule
  // AND there are no override dates available to book.
  const noDeliveryDaysConfigured = allowedDeliveryDays.length === 0 && pickableSet.size === 0;

  return {
    navigate,
    profile,
    loading,
    submitting,
    // location
    selectedLocation,
    setSelectedLocation,
    selectedLocationData,
    // date/time
    deliveryDate,
    setDeliveryDate,
    deliveryTime,
    setDeliveryTime,
    isDateDisabled,
    // extra info
    poNumber,
    setPoNumber,
    orderNotes,
    setOrderNotes,
    // totals + validation
    cartTotal,
    minimumOrder,
    unavailableItems,
    hasUnavailableItems,
    isValidOrder,
    noDeliveryDaysConfigured,
    // over-ordering controls
    overCapLines,
    capGatingActive,
    capReasons: capInsights?.cap_reasons,
    capReason,
    setCapReason: (value) => { setReasonTouched(true); setCapReason(value); },
    capNote,
    setCapNote,
    capReasonMissing: capReasonMissing && reasonTouched,
    setItemQuantity,
    // cart helpers (from context) needed by summary card
    cart,
    isProductAvailableForDate,
    getDayName,
    formatAvailableDays,
    getCartSavings,
    handleRemoveItem,
    handleSubmitOrder,
  };
};
